/**
 * Sentry Error Monitoring Configuration
 * 
 * Initializes Sentry for error tracking with user context, route tracking,
 * and release version correlation.
 */

import * as Sentry from '@sentry/react';
import { env } from './env';
import { hashUserId } from './userHash';

/**
 * Get build version from environment
 */
function getBuildVersion(): string {
  return import.meta.env.VITE_BUILD_VERSION || 
         import.meta.env.VITE_APP_VERSION || 
         'unknown';
}

/**
 * Initialize Sentry error monitoring
 * 
 * Should be called before React renders in main.tsx
 */
export async function initSentry(): Promise<void> {
  // Only initialize if DSN is provided
  if (!env.sentryDsn) {
    console.warn('[Sentry] DSN not provided, error monitoring disabled');
    return;
  }

  const buildVersion = getBuildVersion();
  const environment = import.meta.env.MODE || import.meta.env.NODE_ENV || 'development';

  Sentry.init({
    dsn: env.sentryDsn,
    environment,
    release: buildVersion,
    
    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // React integration
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Error filtering and enhancement
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    beforeSend(event, _hint) {
      // Add build version as tag
      if (event.tags) {
        event.tags.build_version = buildVersion;
      } else {
        event.tags = { build_version: buildVersion };
      }
      
      // Add route as tag if available
      if (typeof window !== 'undefined' && window.location) {
        if (event.tags) {
          event.tags.route = window.location.pathname;
        } else {
          event.tags = { route: window.location.pathname };
        }
      }
      
      return event;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      // Chrome extensions
      'chrome-extension://',
      'moz-extension://',
    ],
    
    // Don't send errors in development unless explicitly enabled
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',
  });

  console.log('[Sentry] Initialized', {
    environment,
    release: buildVersion,
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',
  });
}

/**
 * Set user context in Sentry
 * 
 * @param userId - The user ID to set (will be hashed)
 */
export async function setSentryUser(userId: string | null): Promise<void> {
  if (!userId) {
    Sentry.setUser(null);
    return;
  }

  try {
    // Hash userId for privacy
    const hashedUserId = await hashUserId(userId);
    
    Sentry.setUser({
      id: hashedUserId,
      // Don't include email or other PII
    });
    
    console.log('[Sentry] User context set', { hashedUserId: hashedUserId.substring(0, 8) + '...' });
  } catch (error) {
    console.error('[Sentry] Failed to set user context:', error);
  }
}

/**
 * Set route context in Sentry
 * 
 * @param route - The current route pathname
 */
export function setSentryRoute(route: string): void {
  Sentry.setTag('route', route);
  Sentry.setContext('navigation', {
    pathname: route,
    href: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

/**
 * Capture an exception with context
 * 
 * @param error - The error to capture
 * @param errorInfo - Additional error info (e.g., from React ErrorBoundary)
 */
export function captureException(error: Error, errorInfo?: React.ErrorInfo): void {
  if (errorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  } else {
    Sentry.captureException(error);
  }
}
